/**
 * Shared BI filter bar + export menu for the Analytics hub (Phase 2D).
 * Owns department/batch/date-range state via props from AnalyticsHubPage.
 */
import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export const ALL = '__all__';

export function buildQS(f) {
  const p = new URLSearchParams();
  if (f.departmentId !== ALL) p.set('department_id', f.departmentId);
  if (f.batchId !== ALL) p.set('batch_id', f.batchId);
  if (f.dateFrom) p.set('date_from', f.dateFrom);
  if (f.dateTo) p.set('date_to', f.dateTo);
  const s = p.toString();
  return s ? `?${s}` : '';
}

const REPORTS = [
  { key: 'risk', label: 'Risk Dashboard' },
  { key: 'attendance', label: 'Attendance by Batch' },
  { key: 'assessments', label: 'Assessment Comparison' },
  { key: 'departments', label: 'Department Comparison' },
];

export default function BIFilterBar({ filters, onChange }) {
  const { get } = useApi();
  const [departments, setDepartments] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    get('/trainees/departments?include_inactive=true')
      .then(r => setDepartments(r.departments || [])).catch(() => {});
    get('/trainees/batches')
      .then(r => setBatches(r.batches || [])).catch(() => {});
  }, [get]);

  const visibleBatches = filters.departmentId === ALL
    ? batches
    : batches.filter(b => b.department_id === filters.departmentId);

  const download = async (report, format) => {
    try {
      const qs = buildQS(filters).replace('?', '&');
      const blob = await get(`/analytics/bi/export?report=${report}&format=${format}${qs}`,
        { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lumyn_${report}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch { toast.error('Export failed'); }
  };

  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="bi-filter-bar">
      <Select value={filters.departmentId}
              onValueChange={v => onChange({ ...filters, departmentId: v, batchId: ALL })}>
        <SelectTrigger className="w-48" data-testid="bi-filter-department"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All departments</SelectItem>
          {departments.map(d => <SelectItem key={d.department_id} value={d.department_id}>{d.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={filters.batchId}
              onValueChange={v => onChange({ ...filters, batchId: v })}>
        <SelectTrigger className="w-44" data-testid="bi-filter-batch"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All batches</SelectItem>
          {visibleBatches.map(b => <SelectItem key={b.batch_id} value={b.batch_id}>{b.name}</SelectItem>)}
        </SelectContent>
      </Select>
      <Input type="date" className="w-38" value={filters.dateFrom}
             onChange={e => onChange({ ...filters, dateFrom: e.target.value })}
             data-testid="bi-filter-from" />
      <Input type="date" className="w-38" value={filters.dateTo}
             onChange={e => onChange({ ...filters, dateTo: e.target.value })}
             data-testid="bi-filter-to" />
      <div className="ml-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="bi-export-btn">
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            {REPORTS.map((r, i) => (
              <div key={r.key}>
                {i > 0 && <DropdownMenuSeparator />}
                <DropdownMenuLabel className="text-xs">{r.label}</DropdownMenuLabel>
                <div className="flex gap-1 px-2 pb-1.5">
                  {['csv', 'xlsx', 'pdf'].map(f => (
                    <DropdownMenuItem key={f} className="px-2 py-1 text-xs uppercase font-medium cursor-pointer border rounded"
                                      onClick={() => download(r.key, f)}
                                      data-testid={`export-${r.key}-${f}`}>
                      {f}
                    </DropdownMenuItem>
                  ))}
                </div>
              </div>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
